import { useTranslation } from "react-i18next";
import { BranchAuthenticationType } from "../types/branch";
import { getCurrentBranch } from "./branchStore";

/**
 * Provides a custom translation hook that overrides translation keys based on certain conditions.
 * Specifically, if the branch's authentication type is KYC, it uses the KYC namespace for translations
 * and falls back to the default translation if no override is found.
 */
export function useCustomTranslation() {
  const { t, i18n } = useTranslation();
  // IMPORTANT: We intentionally read the branch via getCurrentBranch() instead of useBranch().
  // Why?
  // - useCustomTranslation() is also consumed by useI18nProvider(), which is created BEFORE
  //   <BranchProvider> is mounted (Refine requires the i18nProvider at mount time).
  // - If we tried to use the React context (useBranch()), it would be undefined at that moment and
  //   we'd capture a stale "non-KYC" state in the translate function's closure.
  // - The tiny branchStore (getCurrentBranch/setCurrentBranch) lets us read the latest branch value
  //   at call-time, even outside the React tree. BranchProvider updates this store and triggers
  //   i18n.changeLanguage(i18n.language) to re-run translations with the correct overrides.
  const branch = getCurrentBranch();

  const isKyc = branch?.authenticationType === BranchAuthenticationType.KYC;

  const tWithOverride: typeof t = (key: any, options?: any) => {
    if (isKyc) {
      const tKyc = i18n.getFixedT(i18n.language, "kyc");
      const overridden = tKyc(key, {
        ...(options || {}),
        defaultValue: undefined,
      });

      if (overridden !== key) {
        return overridden;
      }
    }

    return t(key, options);
  };

  return { t: tWithOverride, i18n };
}
